import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MicNoneIcon from '@mui/icons-material/MicNone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SerializedError } from '@reduxjs/toolkit';
import { Stack } from '@mui/material';
import useAnalytics from 'services/hooks/analytics/useAnalytics';
import { events } from 'services/analytics';
import UploadUserLoop from '../UploadUserLoop/UploadUserLoop';
import RecordUserLoop from '../RecordUserLoop';
import { StyledBackButton } from '../../styled';
import {
  StyledIconWrapper,
  StyledPickerModeButton,
  StyledPickerModeButtonDescription,
  StyledPickerModeButtonInner,
  StyledPickerModeButtonTitle,
  StyledPickerModeContainer,
} from './styled';
import { FilePickerMode } from './constants';

interface LoopFileSelectorProps {
  onAddFile: (file: File) => void;
  onError: (file: File, error: SerializedError, duration?: number) => void;
}

const LoopFileSelector = ({ onAddFile, onError }: LoopFileSelectorProps) => {
  const { track } = useAnalytics();
  const { t } = useTranslation();

  const [filePickerMode, setFilePickerMode] = useState<FilePickerMode | null>(
    null
  );

  const handleUploadFileClick = () => {
    const mode = FilePickerMode.UPLOAD;
    setFilePickerMode(mode);
    track(events.audioClipSelectionClick({ clipType: mode }));
  };

  const handleRecordFileClick = () => {
    const mode = FilePickerMode.RECORD;
    setFilePickerMode(mode);
    track(events.audioClipSelectionClick({ clipType: mode }));
  };

  if (filePickerMode === FilePickerMode.UPLOAD) {
    return (
      <>
        <StyledBackButton
          onClick={() => setFilePickerMode(null)}
          startIcon={<ArrowBackIcon />}
        >
          {t('aiMusicGenerator:userLoopUploadModal:back')}
        </StyledBackButton>
        <UploadUserLoop onAddFile={onAddFile} onError={onError} />
      </>
    );
  }

  if (filePickerMode === FilePickerMode.RECORD) {
    return (
      <>
        <StyledBackButton
          onClick={() => setFilePickerMode(null)}
          startIcon={<ArrowBackIcon />}
        >
          {t('aiMusicGenerator:userLoopUploadModal:back')}
        </StyledBackButton>
        <RecordUserLoop onAddFile={onAddFile} onError={onError} />
      </>
    );
  }

  return (
    <StyledPickerModeContainer>
      <StyledPickerModeButton onClick={handleUploadFileClick}>
        <StyledPickerModeButtonInner>
          <StyledIconWrapper>
            <UploadFileIcon />
          </StyledIconWrapper>
          <Stack>
            <StyledPickerModeButtonTitle>
              {t(
                'aiMusicGenerator:userLoopUploadModal:loopFileSelector:upload:title'
              )}
            </StyledPickerModeButtonTitle>
            <StyledPickerModeButtonDescription>
              {t(
                'aiMusicGenerator:userLoopUploadModal:loopFileSelector:upload:description'
              )}
            </StyledPickerModeButtonDescription>
          </Stack>
        </StyledPickerModeButtonInner>
      </StyledPickerModeButton>

      <StyledPickerModeButton onClick={handleRecordFileClick}>
        <StyledPickerModeButtonInner>
          <StyledIconWrapper>
            <MicNoneIcon />
          </StyledIconWrapper>
          <Stack>
            <StyledPickerModeButtonTitle>
              {t(
                'aiMusicGenerator:userLoopUploadModal:loopFileSelector:record:title'
              )}
            </StyledPickerModeButtonTitle>
            <StyledPickerModeButtonDescription>
              {t(
                'aiMusicGenerator:userLoopUploadModal:loopFileSelector:record:description'
              )}
            </StyledPickerModeButtonDescription>
          </Stack>
        </StyledPickerModeButtonInner>
      </StyledPickerModeButton>
    </StyledPickerModeContainer>
  );
};

export default LoopFileSelector;
